import {Component, inject, input, model, resource, signal} from '@angular/core';
import {take} from 'rxjs/operators';
import {CommonModule} from '@angular/common';
import {DxDataGridModule, DxLoadPanelModule, DxPopupModule, DxTextBoxModule} from 'devextreme-angular';
import {confirm} from 'devextreme/ui/dialog';
import {SharedModule} from '../../../../shared/shared.module';
import {ButtonThemeEnum} from '../../../../shared/ui/button/button-theme-enum';
import {PrixDto} from '../../../../core/dtos/prix-dto';
import {AvoirFournisseurDto} from '../../../../core/dtos/facturation/fournisseurs/avoirs/avoir-fournisseur-dto';
import {GraphQLService} from '../../../../core/services/technique/graphql.service';
import {AvoirExterneService} from '../../../../core/services/entities/avoir-externe.service';
import {RapprochementAvoirExterneStatutEnum} from '../../../../core/enums/facturation/fournisseur/rapprochement-avoir-externe-statut-enum';

@Component({
  selector: 'yo-rapprochement-avoir-externe',
  templateUrl: './rapprochement-avoir-externe-dialog.component.html',
  styleUrls: ['./rapprochement-avoir-externe-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, DxPopupModule, DxDataGridModule, DxLoadPanelModule, DxTextBoxModule, SharedModule]
})
export class RapprochementAvoirExterneDialogComponent {

  protected readonly ButtonThemeEnum = ButtonThemeEnum;

  private readonly graphQlSvc = inject(GraphQLService);
  private readonly avoirExterneSvc = inject(AvoirExterneService);

  readonly displayDialog = model<boolean>(false);
  readonly avoirExterneId = input<number | null>(null);
  readonly avoirExterneNumero = input<string | null>(null);
  readonly avoirExterneSiteId = input<number | null>(null);

  readonly numeroAvoirFilter = signal('');

  readonly avoirFournisseurListResource = resource<AvoirFournisseurDto[], { open: boolean; numeroAvoir: string; siteId: number | null }>({
    params: () => ({open: this.displayDialog(), numeroAvoir: this.numeroAvoirFilter(), siteId: this.avoirExterneSiteId()}),
    loader: async ({params}) => {
      if (!params.open || params.siteId == null) return [];
      let filtersStr = `siteIds: [${params.siteId}]`;
      if (params.numeroAvoir?.trim()) filtersStr += `, numeroAvoir: "${params.numeroAvoir.trim()}"`;
      const response: any = await this.graphQlSvc.sendQuery(`
        {
          allAvoirsFournisseur(filters: { ${filtersStr} }) {
            ${AvoirFournisseurDto.getAllProperties()}
          }
        }
      `).pipe(take(1)).toPromise();
      return (response?.allAvoirsFournisseur ?? []) as AvoirFournisseurDto[];
    }
  });

  closeDialog = (): void => this.displayDialog.set(false);

  getPrixDisplayValue = (prix: PrixDto): string =>
    prix ? `${prix.value?.toFixed(4)} ${prix.uniteMonetaire}` : '';

  formatBonLivraisonList = (list: string[]): string =>
    list?.filter(n => n?.trim()).join(', ') ?? '';

  selectAvoirFournisseur = async (avoirFournisseur: AvoirFournisseurDto): Promise<void> => {
    const avoirExterneId = this.avoirExterneId();
    if (!avoirExterneId) return;

    const isConfirmed: boolean = await confirm(
      `Voulez-vous rapprocher l'avoir externe n° ${this.avoirExterneNumero()} avec l'avoir fournisseur n° ${avoirFournisseur.code} ?`,
      "Rapprochement manuel"
    );
    if (!isConfirmed) return;

    this.avoirExterneSvc.rapprocherManuellement(avoirExterneId, avoirFournisseur.id).subscribe(() => {
      this.avoirExterneSvc.avoirExterneResource.update(list => (list ?? []).map(a => a.id !== avoirExterneId ? a : {
        ...a,
        rapprochementStatut: RapprochementAvoirExterneStatutEnum.RAPPROCHEMENT_OK,
        rapprochementList: (a.rapprochementList ?? []).map((r, idx) => idx === 0
          ? {...r, avoirFournisseurId: avoirFournisseur.id, avoirNumero: avoirFournisseur.code as string}
          : r)
      }));
      this.closeDialog();
    });
  };
}
